import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Field';
import { Alert } from '@/components/ui/Alert';
import { Card, CardBody } from '@/components/ui/Card';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/useAuth';
import { isSupabaseConfigured } from '@/lib/supabase/client';
import { ROUTES, landingRouteForRole } from '@/config/navigation';

export default function SignInPage() {
  const { signIn, isAuthenticated, role } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Une fois la session ouverte, on attend que le profil soit chargé pour
  // connaître le rôle : chacun atterrit dans son propre espace, pas sur
  // l'accueil public.
  if (isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) return <Navigate to={from} replace />;
    if (!role) return <FullPageSpinner />;
    return <Navigate to={landingRouteForRole(role)} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signIn(email, password);
      // La redirection est gérée ci-dessus, quand le rôle est connu.
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Connexion impossible.');
      setLoading(false);
    }
  }

  return (
    <div className="cf-container flex justify-center py-12">
      <div className="w-full max-w-md">
        <h1 className="text-2xl font-semibold text-ink-950">Connexion</h1>
        <p className="mt-1 text-sm text-ink-600">Accédez à votre espace BD Innovation.</p>

        {!isSupabaseConfigured && (
          <Alert tone="warning" title="Supabase non configuré" className="mt-5">
            La connexion nécessite un projet Supabase. Renseignez les variables d’environnement puis
            relancez le serveur de développement.
          </Alert>
        )}

        <Card className="mt-5">
          <CardBody>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                label="Adresse e-mail"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                label="Mot de passe"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              {error && <Alert tone="danger">{error}</Alert>}

              <Button
                type="submit"
                size="lg"
                className="w-full"
                loading={loading}
                disabled={!isSupabaseConfigured}
              >
                Se connecter
              </Button>
            </form>
          </CardBody>
        </Card>

        <p className="mt-5 text-center text-sm text-ink-600">
          Pas encore de compte ?{' '}
          <Link to={ROUTES.signUp} className="font-medium text-brand-700 hover:underline">
            Créer un compte
          </Link>
        </p>
      </div>
    </div>
  );
}
